import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRamadan } from '../context/RamadanContext';

const QuranTracker: React.FC = () => {
  const { t } = useTranslation();
  const { dayData, updateQuranTracker, currentDay } = useRamadan();
  
  // Add local state to track input values before saving
  const [localQuranData, setLocalQuranData] = useState({
    ayat: '',
    page: '',
    para: '',
  });

  // Synchronize local state with dayData when day changes or initial load
  useEffect(() => {
    if (dayData?.quranTracker) {
      setLocalQuranData({
        ayat: dayData.quranTracker.ayat || '',
        page: dayData.quranTracker.page || '',
        para: dayData.quranTracker.para || '',
      });
    }
  }, [currentDay, dayData?.quranTracker]);

  // Update local state without immediately saving to context
  const handleInputChange = useCallback((field: 'ayat' | 'page' | 'para', value: string) => {
    setLocalQuranData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Save to context on blur
  const handleBlur = useCallback((field: 'ayat' | 'page' | 'para') => {
    if (dayData && localQuranData[field] !== dayData.quranTracker[field]) {
      updateQuranTracker(field, localQuranData[field]);
    }
  }, [dayData, localQuranData, updateQuranTracker]);

  // If dayData is not loaded yet
  if (!dayData) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="border border-[#dde8d6] rounded-lg p-0 bg-white">
      {/* Title with icon */}
      <div className="flex items-center justify-center py-2 space-x-2">
        <div className="bg-[#a6ce86] rounded-lg w-10 h-10 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
          </svg>
        </div>
        <h2 className="text-lg font-bengali font-semibold text-[#4b7a3c]">
          {t('sections.quranTracker')}
        </h2>
      </div>

      {/* Tracking fields */}
      <div className="grid grid-cols-3 gap-0 border-t border-[#eee]">
        <div className="text-center p-2 border-r border-[#eee]">
          <div className="font-bengali text-sm text-[#4b7a3c] mb-1">{t('sections.quranTrackerSubSections.ayat')}</div>
          <div className="bg-[#eaeaea] px-2 py-1 rounded-sm">
            <input
              type="text"
              value={localQuranData.ayat}
              onChange={(e) => handleInputChange('ayat', e.target.value)}
              onBlur={() => handleBlur('ayat')}
              className="bg-transparent w-full text-center focus:outline-none text-[#4b7a3c]"
              placeholder="০"
            />
          </div>
        </div>
        
        <div className="text-center p-2 border-r border-[#eee]">
          <div className="font-bengali text-sm text-[#4b7a3c] mb-1">{t('sections.quranTrackerSubSections.page')}</div>
          <div className="bg-[#eaeaea] px-2 py-1 rounded-sm">
            <input
              type="text"
              value={localQuranData.page}
              onChange={(e) => handleInputChange('page', e.target.value)}
              onBlur={() => handleBlur('page')}
              className="bg-transparent w-full text-center focus:outline-none text-[#4b7a3c]"
              placeholder="০"
            />
          </div>
        </div>
        
        <div className="text-center p-2">
          <div className="font-bengali text-sm text-[#4b7a3c] mb-1">{t('sections.quranTrackerSubSections.para')}</div>
          <div className="bg-[#eaeaea] px-2 py-1 rounded-sm">
            <input
              type="text"
              value={localQuranData.para}
              onChange={(e) => handleInputChange('para', e.target.value)}
              onBlur={() => handleBlur('para')}
              className="bg-transparent w-full text-center focus:outline-none text-[#4b7a3c]"
              placeholder="০"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuranTracker; 